import {
  Component,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { BadgeModule } from 'primeng/badge';
import {
  NotifsAndMessagesService,
  Notification,
  Message,
  NotificationsResponse,
  MessagesResponse,
  CharityNotificationObjectPageActionDataTypes,
} from './notifs-and-messages.service';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import {
  InfiniteListComponent,
  InfiniteListItemDirective,
} from '../../../shared/components/infinite-list/infinite-list.component';
import { PaginationMeta } from '../../../core/models/pagination.model';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DateToJalaliPipe } from '../../../shared/pipes/date-to-jalali.pipe';
import { StringToDatePipe } from '../../../shared/pipes/string-to-date.pipe';
import { SharedModule } from '../../../shared/shared.module';
import { CustomToastComponent } from 'src/app/shared/components/custom-toast/custom-toast.component';

@Component({
  selector: 'notificatoins-and-messages-dropdown',
  templateUrl: './notificatoins-and-messages-dropdown.component.html',
  styleUrls: ['./notificatoins-and-messages-dropdown.component.css'],
  imports: [
    CommonModule,
    TabsModule,
    BadgeModule,
    OverlayBadgeModule,
    InfiniteListComponent,
    InfiniteListItemDirective,
    SharedModule,
    ToastModule,
    CustomToastComponent,
  ],
})
export class NotificatoinsAndMessagesDropdownComponent {
  @Input() notifications!: NotificationsResponse;
  @Input() messages!: MessagesResponse;
  @Output() notificationsChange = new EventEmitter<NotificationsResponse>();
  @Output() messagesChange = new EventEmitter<MessagesResponse>();

  messageService = inject(MessageService);
  private router = inject(Router);
  constructor(
    private notifsService: NotifsAndMessagesService,
    private cdr: ChangeDetectorRef,
  ) {}
  ngOnInit() {}

  get unseenNotifCount() {
    return this.notifications.extra.unseen_notifications_count;
  }

  get unseenMessageCount() {
    return this.messages.extra.unseen_messages_count;
  }

  // Sort unseen first, then by date (descending)
  readonly notifSortFn = (a: Notification, b: Notification) => {
    if (!a.seen && b.seen) return -1;
    if (a.seen && !b.seen) return 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  };

  readonly messageSortFn = (a: Message, b: Message) => {
    if (!a.seen && b.seen) return -1;
    if (a.seen && !b.seen) return 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  };

  // Wrappers for the infinite list component
  fetchNotificationsPage = (page: number, params?: any) =>
    this.notifsService.getNotifications(page, params);

  fetchMessagesPage = (page: number, params?: any) =>
    this.notifsService.getMessages(page, params);

  onNotifItemsChange(items: Notification[]) {
    this.notifications.results = items;
    this.cdr.detectChanges();
    this.notificationsChange.emit(this.notifications);
  }

  onMessageItemsChange(items: Message[]) {
    this.messages.results = items;
    this.cdr.detectChanges();
    this.messagesChange.emit(this.messages);
  }

  async markNotificationSeen(notif: Notification) {
    await this.notifsService.markNotificationAsSeen(notif.id);
    notif.seen = true;
    this.notifications.extra.unseen_notifications_count--;
    this.cdr.detectChanges();
    this.notificationsChange.emit(this.notifications);
  }

  async deleteNotification(notif: Notification) {
    await this.notifsService.deleteNotification(notif.id);
    this.notifications.results = this.notifications.results.filter(
      (e) => e.id !== notif.id,
    );
    if (!notif.seen) this.notifications.extra.unseen_notifications_count--;
    this.cdr.detectChanges();
    this.notificationsChange.emit(this.notifications);
  }

  deleteAllNotifications() {
    const lastFetchedId = this.notifications.results.length
      ? this.notifications.results[0].id
      : 0;
    this.notifsService
      .deleteAllNotifications(lastFetchedId)
      .then(() => {
        this.notifications.results = [];
        this.notifications.extra.unseen_notifications_count = 0;
        this.cdr.detectChanges();
        this.notificationsChange.emit(this.notifications);
      })
      .catch((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'خطا',
          detail: error.error.message,
          life: 4000,
          key: 'notificatoins-and-messages-dropdown',
        });
      });
  }
  seeAllNotifications() {
    const lastFetchedId = this.notifications.results.length
      ? this.notifications.results[0].id
      : 0;
    this.notifsService.seeAllNotifications(lastFetchedId).then(() => {
      this.notifications.results.forEach((notif) => (notif.seen = true));
    });
    this.notifications.extra.unseen_notifications_count = 0;
    this.cdr.detectChanges();
    this.notificationsChange.emit(this.notifications);
  }

  async openNotification(notif: Notification) {
    if (!notif.seen) await this.markNotificationSeen(notif);
    if (!notif.action_type || !notif.action_data) {
      return;
    }

    switch (notif.action_type) {
      case 2:
        this.openNotificationObject(notif);
    }
  }
  async openNotificationObject(notif: Notification) {
    if (!notif.seen) await this.markNotificationSeen(notif);
    switch (notif.action_type) {
      case 2:
        // Open a link
        switch (notif.action_data.item_type) {
          case 0:
            window.open('workshop/workshops', '_self');
            break;
          case 1:
            window.open('e-workshop/e-workshops', '_self');
            break;
          case 2:
            window.open('profile/my-training-history', '_self');
            break;
          case 3:
            window.open(
              'e-workshop/e-workshops/' + notif.action_data.item_id,
              '_self',
            );
        }
    }
    console.log('Opening notification:', notif);
  }

  async markMessageAsSeen(message: Message) {
    await this.notifsService.markMessageAsSeen(message.id);
    message.seen = true;
    this.messages.extra.unseen_messages_count--;
    this.cdr.detectChanges();
    this.messagesChange.emit(this.messages);
  }

  deleteMessage(message: Message) {
    this.notifsService.deleteMessage(message.id).then(() => {
      this.messages.results = this.messages.results.filter(
        (e) => e.id !== message.id,
      );
      if (!message.seen) this.messages.extra.unseen_messages_count--;
      this.cdr.detectChanges();
      this.messagesChange.emit(this.messages);
    });
  }

  deleteAllMessages() {
    const lastFetchedId = this.messages.results.length
      ? this.messages.results[0].id
      : 0;
    this.notifsService
      .deleteAllMessages(lastFetchedId)
      .then(() => {
        this.messages.results = [];
        this.messages.extra.unseen_messages_count = 0;
        this.cdr.detectChanges();
        this.messagesChange.emit(this.messages);
      })
      .catch((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'خطا',
          detail: error.error.message,
          life: 4000,
          key: 'notificatoins-and-messages-dropdown',
        });
      });
  }

  seeAllMessages() {
    const lastFetchedId = this.messages.results.length
      ? this.messages.results[0].id
      : 0;
    this.notifsService.seeAllMessages(lastFetchedId).then(() => {
      this.messages.results.forEach((message) => (message.seen = true));
    });
    this.messages.extra.unseen_messages_count = 0;
    this.cdr.detectChanges();
    this.messagesChange.emit(this.messages);
  }

  async openMessage(message: Message) {
    if (!message.seen) await this.markMessageAsSeen(message);
    window.open(`message/${message.id}`, '_self');
  }
}
/**
@ROOT = {{baseUrl}}
@auth_token = {{auth_token}}
###
GET {{ROOT}}/notifications/
Authorization: Bearer {{auth_token}}

###
GET {{ROOT}}/notifications/updates/1/
Authorization: Bearer {{auth_token}}

###
DELETE {{ROOT}}/notifications/delete/1002/
Authorization: Bearer {{auth_token}}

###
DELETE {{ROOT}}/notifications/delete-all/
Authorization: Bearer {{auth_token}}
Content-Type: application/json

{
    "last_fetched_id": 1008
}

###
PATCH {{ROOT}}/notifications/see/1006/
Authorization: Bearer {{auth_token}}


###
PATCH {{ROOT}}/notifications/see-all/
Authorization: Bearer {{auth_token}}
Content-Type: application/json

{
    "last_fetched_id": 1008
}
*/
